import { ValidationObserver, ValidationProvider } from "vee-validate";
import { MailingTypes } from "@/enums/MailingTypes";
import { MailingMethods } from "@/enums/MailingMethods";
import AddMailingListDialog from "@/views/settings/mailing/mailingList/addMailingListDialog/AddMailingListDialog.vue";
import { FamilyHubAppStatus } from "@/enums/FamilyHubAppStatus";
import { FamilyHubUserStatus } from "@/enums/FamilyHubUserStatus";
import { ResidentStatus } from "@/enums/ResidentStatus";

export default {
  name: "MailingLists",
  components: {
    ValidationObserver,
    ValidationProvider,
    AddMailingListDialog
  },
  data() {
    return {
      search: "",
      mailingTypes: MailingTypes,
      mailingMethods: MailingMethods,
      sortBy: "type",
      sortDesc: true,
      headers: [
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Name'],
          value: "name",
          sortable: false,
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Type'],
          value: "type",
          sortable: false,
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['Settings.MailingList.NumberOfContacts'],
          value: "numberOfContacts",
          sortable: false,
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['Settings.MailingList.InputMethod'],
          value: "method",
          sortable: false,
        },
      ]
    }
  },
  computed: {
    currentFacility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    facilityMailingLists() {
      return this.$store.state.facilityModule.facilityMailingLists;
    },
    staff() {
      return this.$store.state.facilityModule.selectedFacilityUsers;
    },
    residents() {
      return this.$store.state.residentModule.residentsShortData;
    },
    allFamilyMembers() {
      return this.$store.state.residentModule.familyMembers;
    },
    archivedFamilyMembers() {
      return this.allFamilyMembers.filter(x => (x.status === FamilyHubUserStatus.Archived || (x.residents || []).some(y => y.status === ResidentStatus.Departed)))
    },
    pendingFamilyMembers() {
      return this.allFamilyMembers.filter(x=>x.appStatus === FamilyHubAppStatus.Pending);
    },
    EPOAFamilyMembers() {
      return this.allFamilyMembers.filter(family => family?.financialEPOA?.some(epoa => epoa.status === true) == true)
    },
    familyCanInviteOtherFamily() {
      return this.allFamilyMembers.filter(family => family?.isCanInvite == true)
    },
    familyMembersNotUsingTheApp() {
      return this.allFamilyMembers.filter(family => !family?.invitedToFamilyApp)
    },
    familyMembersWithTheAppActivated() {
      return this.allFamilyMembers.filter(family => family.appStatus === FamilyHubAppStatus.Activated)
    },
    familyMembersWithWings(){
     return this.residents.filter((resident: any) =>  resident.status === ResidentStatus.ActiveResident && resident?.familyHubUsersIds?.length > 0 && this.wingsIds.includes(resident.wingId))
        .map((resident: any) => 
            this.allFamilyMembers.filter((familyMember: any) => resident?.familyHubUsersIds?.includes(familyMember.id))
        .map((user: any) => ({
            ...user, 
            wingId: resident.wingId,
            wingName: resident.wingName })
        ))
        .flat()
        .reduce((unique: any[], user: any) => { if (!unique.some((u) => u.id === user.id)) { unique.push(user) } return unique }, []);
    },
    familyMembersPerLevelOfCare(){
      return this.residents.filter((resident: any) =>  resident.status === ResidentStatus.ActiveResident && resident?.familyHubUsersIds?.length > 0 && this.careLevelsWingsIds?.includes(resident.wingId))
        .map((resident: any) => 
            this.allFamilyMembers.filter((familyMember: any) => resident?.familyHubUsersIds?.includes(familyMember.id))
        .map((user: any) => ({
            ...user, 
            careLevelIds: this.careLevels.filter((careLevel: any) => careLevel.wingsIds.includes(resident.wingId)).map((careLevel: any) => careLevel.id)})
        ))
        .flat()
        .reduce((unique: any[], user: any) => { if (!unique.some((u) => u.id === user.id)) { unique.push(user) } return unique }, []);
    },
    wings() {
      return this.$store.state.eventLocationModule.eventWings;
    },
    careLevels() {
      return this.$store.state.careLevelsModule.careLevels
    },
    careLevelsIds() {
      return this.$store.state.careLevelsModule.careLevels.map((x) => x.id);
    },
    careLevelsWingsIds(){
      return [...new Set(this.careLevels.flatMap(level => level.wingsIds))];
    },
    wingsIds() {
      return this.wings.map((wing: any) => wing.id);
    },
    mailingLists(){
      const list = [...this.facilityMailingLists]

      for(const mailingList of list){
        if(mailingList.type === MailingTypes.Staff){
          mailingList.numberOfContacts = this.staff.length;
        } else if(mailingList.type === MailingTypes.Residents){
          mailingList.numberOfContacts = this.residents.length;
        } else if(mailingList.type === MailingTypes.Family){
          mailingList.numberOfContacts = this.allFamilyMembers.length;
        } else if(mailingList.type === MailingTypes.PendingFamilyMembers){
          mailingList.numberOfContacts = this.pendingFamilyMembers.length;
        } else if(mailingList.type === MailingTypes.Custom){
          mailingList.numberOfContacts = mailingList.to?.length || 0
        } else if(mailingList.type === MailingTypes.ArchivedFamilyMembers){
          mailingList.numberOfContacts = this.archivedFamilyMembers.length;
        } else if(mailingList.type === MailingTypes.FamilyWithFEPOA){
          mailingList.numberOfContacts = this.EPOAFamilyMembers.length;
        } else if(mailingList.type === MailingTypes.FamilyCanInviteOtherFamily){
          mailingList.numberOfContacts = this.familyCanInviteOtherFamily.length;
        } else if(mailingList.type === MailingTypes.FamilyMembersNotUsingTheApp){
          mailingList.numberOfContacts = this.familyMembersNotUsingTheApp.length;
        } else if(mailingList.type === MailingTypes.FamilyMembersWithTheAppActivated){
          mailingList.numberOfContacts = this.familyMembersWithTheAppActivated.length;
        } else if(mailingList.type === MailingTypes.FamilyMembersPerWing){
          mailingList.numberOfContacts = this.familyMembersWithWings.filter((family) => family.wingId === mailingList.wingId).length;
        } else if(mailingList.type === MailingTypes.FamilyMembersPerLevelOfCare){
          mailingList.numberOfContacts = this.familyMembersPerLevelOfCare.filter((family) => family.careLevelIds.includes(mailingList.careLevelId)).length;
        }
      }

      return list
    }
  },
  methods: {
    handleClickMailing(row) {
      this.$router.push("/settings/mailingList/" + row.id);
    },
    open() {
      this.$refs.addMailingListDialog.open();
    },
    itemRowBackground(item) {
      return item.type !== this.mailingTypes.Custom ? 'defaultType' : ''
    }
  },
  async created() {
    this.$store.dispatch("getFacilityMailingLists");

   if(!this.staff?.length){
      this.$store.dispatch("getCurrentFacilityUsers");
    }

    if(!this.familyMembers?.length){
      this.$store.dispatch("getFamilyMembers")
    }

    if(!this.careLevels?.length){
      this.$store.dispatch("getCareLevels")
    }

    if(!this.wings?.length){
      this.$store.dispatch("getEventWings")
    }
  }
}